<template>
  <div
    v-if="allowedCategories.length > 1"
    class="categories-megamenu-wrapper"
    ref="wrapperRef"
  >
    <!-- Trigger Button (Apple Style Pill) -->
    <button
      type="button"
      class="categories-trigger-btn hidden md:inline-flex"
      :class="{
        'categories-trigger-btn--open': isOpen,
        'categories-trigger-btn--active': isCategoryRouteActive,
      }"
      @click="toggleDropdown"
      aria-haspopup="true"
      :aria-expanded="isOpen"
    >
      <i class="fa-solid fa-shapes text-xs text-[#386f99]"></i>
      <span class="categories-trigger-label">Categories</span>
      <i
        class="fa-solid fa-chevron-down categories-trigger-chevron"
        :class="{ 'rotate-180': isOpen }"
      ></i>
    </button>

    <!-- Backdrop Overlay (Frosted Glass) -->
    <div
      v-if="isOpen"
      class="megamenu-backdrop"
      :class="
        isMenuOpenSidebar ? 'megamenu-sidebar-open' : 'megamenu-sidebar-closed'
      "
      @click="closeDropdown"
    ></div>

    <!-- Mega-Menu Full-Width Dropdown Panel (Apple-Style) -->
    <transition name="megamenu-slide">
      <div
        v-if="isOpen"
        class="megamenu-panel shadow-2xl"
        :class="
          isMenuOpenSidebar
            ? 'megamenu-sidebar-open'
            : 'megamenu-sidebar-closed'
        "
        role="menu"
        @click.stop
      >
        <div class="megamenu-container">
          <!-- Top Header Bar: Categories, Filter Tabs & Apple Spotlight Search -->
          <div class="megamenu-topbar">
            <!-- Left: Title & Quick Section Filter Tabs -->
            <div class="flex items-center gap-3 flex-wrap">
              <div class="topbar-icon-box">
                <i class="fa-solid fa-shapes"></i>
              </div>
              <div class="flex items-center gap-2">
                <span class="topbar-title">Categories</span>
                <span class="topbar-count-badge">{{ totalItemsCount }}</span>
              </div>
            </div>

            <!-- Mobile Close Button -->
            <button
              @click="closeDropdown"
              class="flex md:hidden items-center justify-center w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors ml-auto"
              title="Close menu"
              type="button"
            >
              <i class="fa-solid fa-xmark text-xs"></i>
            </button>

            <!-- <div class="search-box-wrapper">
              <div class="relative w-full sm:w-72">
                <i
                  class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none"
                ></i>
                <input
                  type="text"
                  v-model="searchQuery"
                  ref="searchInputRef"
                  placeholder="Search pages & features..."
                  class="search-input"
                />
                <button
                  v-if="searchQuery"
                  @click="searchQuery = ''"
                  class="search-clear-btn"
                  title="Clear search"
                  type="button"
                >
                  <i class="fa-solid fa-xmark text-xs"></i>
                </button>
              </div>
            </div> -->
          </div>

          <div class="megamenu-content-wrapper">
            <div
              v-if="searchQuery && filteredCategories.length === 0"
              class="no-results-state"
            >
              <div class="no-results-icon">
                <i class="fa-solid fa-magnifying-glass"></i>
              </div>
              <p class="no-results-title">
                No features found matching "<span class="text-slate-800">{{
                  searchQuery
                }}</span
                >"
              </p>
              <button
                type="button"
                class="reset-search-btn"
                @click="
                  searchQuery = '';
                  activeTab = 'all';
                "
              >
                Reset search
              </button>
            </div>

            <div v-else class="megamenu-main-grid">
              <!-- Category Columns Container -->
              <div
                class="categories-columns-grid"
                :class="[
                  `grid-cols-${filteredCategories.length}`,
                  { 'has-featured': showFeaturedCard },
                ]"
              >
                <div
                  v-for="category in filteredCategories"
                  :key="category.id"
                  class="category-column"
                >
                  <!-- Category Column Header -->
                  <div
                    class="category-header"
                    :class="{
                      'category-header--collapsible': isTabletOrMobile,
                      'category-header--open': isCategorySectionOpen(category.id),
                    }"
                    @click="toggleCategorySection(category.id)"
                  >
                    <div class="flex items-center gap-2 min-w-0">
                      <div class="category-header-icon">
                        <i :class="category.icon"></i>
                      </div>
                      <div class="min-w-0">
                        <div class="category-title truncate">{{ category.title }}</div>
                        <div
                          v-if="category.description"
                          class="category-subtitle truncate"
                        >
                          {{ category.description }}
                        </div>
                      </div>
                    </div>
                    <div class="flex items-center gap-2 shrink-0">
                      <span
                        v-if="category.badge && category.badge.text !== ''"
                        class="category-badge"
                        :class="`category-badge--${category.badge.type}`"
                      >
                        {{ category.badge.text }}
                      </span>
                      <i
                        v-if="isTabletOrMobile"
                        class="fa-solid fa-chevron-down category-collapse-chevron"
                        :class="{ 'rotate-180': isCategorySectionOpen(category.id) }"
                      ></i>
                    </div>
                  </div>

                  <!-- Category Items List (Untitled UI / Apple Style) -->
                  <ul
                    v-show="!isTabletOrMobile || isCategorySectionOpen(category.id)"
                    class="category-items-list"
                  >
                    <li
                      v-for="item in category.items"
                      :key="item.name"
                      class="category-item"
                    >
                      <a
                        v-if="item.isExternal || category.id === 'crash'"
                        :href="getUrlCrash()"
                        target="_blank"
                        class="category-item-link"
                        @click="closeDropdown"
                      >
                        <div class="item-icon-box">
                          <i :class="item.icon"></i>
                        </div>

                        <div class="item-content">
                          <div class="item-title-row">
                            <span
                              class="item-name"
                              v-html="highlightMatch(item.name)"
                            ></span>
                            <span
                              v-if="item.badge && item.badge.text !== ''"
                              class="item-badge"
                              :class="`item-badge--${item.badge.type}`"
                            >
                              {{ item.badge.text }}
                            </span>
                          </div>
                          <span
                            v-if="item.description && !searchQuery"
                            class="item-description"
                          >
                            {{ item.description }}
                          </span>
                          <span
                            v-else-if="searchQuery && item.description"
                            class="item-description"
                            v-html="highlightMatch(item.description)"
                          ></span>
                        </div>

                        <i
                          class="fa-solid fa-arrow-up-right-from-square item-arrow-icon !text-[11px]"
                        ></i>
                      </a>

                      <router-link
                        v-else
                        :to="getItemRouteLocation(item)"
                        class="category-item-link"
                        :class="{
                          'category-item-link--active': isItemActive(
                            item,
                            category,
                          ),
                        }"
                        @click="closeDropdown"
                      >
                        <div class="item-icon-box">
                          <i :class="item.icon"></i>
                        </div>

                        <div class="item-content">
                          <div class="item-title-row">
                            <span
                              class="item-name"
                              v-html="highlightMatch(item.name)"
                            ></span>
                            <span
                              v-if="item.badge && item.badge.text !== ''"
                              class="item-badge"
                              :class="`item-badge--${item.badge.type}`"
                            >
                              {{ item.badge.text }}
                            </span>
                          </div>
                          <span
                            v-if="item.description && !searchQuery"
                            class="item-description"
                          >
                            {{ item.description }}
                          </span>
                          <span
                            v-else-if="searchQuery && item.description"
                            class="item-description"
                            v-html="highlightMatch(item.description)"
                          ></span>
                        </div>

                        <i
                          class="fa-solid fa-chevron-right item-arrow-icon"
                        ></i>
                      </router-link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import {
  categoriesMenuConfig,
  buildNavigationRoute,
} from "@/config/categoriesMenu";
import { useHeaderNavigation } from "@/composables/useHeaderNavigation";
import { isCategoryAllowed, getUrlCrash } from "@/helpers/appConfiguration";

const route = useRoute();
const store = useStore();

const { isMenuOpen, toggleMenu, closeMenu, openMenu } = useHeaderNavigation();
const wrapperRef = ref(null);
const searchInputRef = ref(null);
const searchQuery = ref("");
const activeTab = ref("all");
const config = categoriesMenuConfig;

const windowWidth = ref(
  typeof window !== "undefined" ? window.innerWidth : 1200,
);
const isTabletOrMobile = computed(() => windowWidth.value <= 1024);

const updateWindowWidth = () => {
  if (typeof window !== "undefined") {
    windowWidth.value = window.innerWidth;
  }
};

const openCategorySections = ref({});

const isCategorySectionOpen = (catId) => {
  if (!isTabletOrMobile.value || searchQuery.value.trim()) return true;
  return Boolean(openCategorySections.value[catId]);
};

const toggleCategorySection = (catId) => {
  if (!isTabletOrMobile.value) return;
  openCategorySections.value = {
    ...openCategorySections.value,
    [catId]: !openCategorySections.value[catId],
  };
};

const isOpen = computed(() => isMenuOpen("categories"));
const isMenuOpenSidebar = computed(() => Boolean(store?.state?.isMenuOpen));

const allowedCategories = computed(() => {
  return config.categories.filter((cat) => isCategoryAllowed(cat.section));
});

const isCategoryRouteActive = computed(() => {
  const currentSection = route?.meta?.section;
  return ["slot", "instant", "livecasino"].includes(currentSection);
});

const totalItemsCount = computed(() => {
  return allowedCategories.value.reduce(
    (acc, cat) => acc + (cat.items ? cat.items.length : 0),
    0,
  );
});

const showFeaturedCard = computed(() => {
  return !searchQuery.value && Boolean(config.featuredCard);
});

/**
 * Filtered categories based on active tab and search query
 */
const filteredCategories = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();

  return allowedCategories.value
    .filter((cat) => {
      if (activeTab.value !== "all" && cat.id !== activeTab.value) {
        return false;
      }
      return true;
    })
    .map((cat) => {
      if (!query) return cat;

      const matchingItems = (cat.items || []).filter((item) => {
        const nameMatch = item.name && item.name.toLowerCase().includes(query);
        const descMatch =
          item.description && item.description.toLowerCase().includes(query);
        const routeMatch =
          item.routeName && item.routeName.toLowerCase().includes(query);
        return nameMatch || descMatch || routeMatch;
      });

      return {
        ...cat,
        items: matchingItems,
      };
    })
    .filter((cat) => cat.items && cat.items.length > 0);
});

const toggleDropdown = () => {
  toggleMenu("categories");
  if (isOpen.value) {
    nextTick(() => {
      if (searchInputRef.value) searchInputRef.value.focus();
    });
  }
};

const closeDropdown = () => {
  if (isOpen.value) {
    closeMenu();
    searchQuery.value = "";
    activeTab.value = "all";
  }
};

const openModal = () => {
  setTimeout(() => {
    openMenu("categories");
    nextTick(() => {
      if (searchInputRef.value) searchInputRef.value.focus();
    });
  }, 20);
};

defineExpose({
  openModal,
  openDropdown: openModal,
  closeDropdown,
});

const highlightMatch = (text) => {
  if (!text) return "";
  const query = searchQuery.value.trim();
  if (!query) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");
  return text.replace(regex, `<mark class="search-highlight">$1</mark>`);
};

const getItemRouteLocation = (item) => {
  return buildNavigationRoute(item, route, store);
};

const getFeaturedRouteLocation = () => {
  return buildNavigationRoute(
    { routeName: config.featuredCard?.targetRouteName || "Home" },
    route,
    store,
  );
};

const isItemActive = (item, category) => {
  if (route.name === item.routeName) return true;
  if (
    category?.section === "slot" &&
    item.routeName === "Home" &&
    (route.name === "Home" || route.path?.endsWith("/slot"))
  ) {
    return true;
  }
  return false;
};

const handleClickOutside = (event) => {
  if (!isOpen.value) return;
  if (wrapperRef.value && !wrapperRef.value.contains(event.target)) {
    closeDropdown();
  }
};

const handleKeyDown = (event) => {
  if (event.key === "Escape" && isOpen.value) {
    closeDropdown();
  }
};

// Body Class management for custom CSS & operator customization
watch(
  isOpen,
  (val) => {
    if (val && isTabletOrMobile.value) {
      // Start with all sections collapsed on mobile/tablet
      openCategorySections.value = {};
    }
    if (typeof document !== "undefined") {
      if (val) {
        document.body.classList.add("categories-megamenu-open");
        document.body.classList.add("megamenu-open");
      } else {
        document.body.classList.remove("categories-megamenu-open");
        document.body.classList.remove("megamenu-open");
      }
    }
  },
  { immediate: true },
);

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleKeyDown);
  window.addEventListener("resize", updateWindowWidth);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleKeyDown);
  window.removeEventListener("resize", updateWindowWidth);
  if (typeof document !== "undefined") {
    document.body.classList.remove("categories-megamenu-open");
    document.body.classList.remove("megamenu-open");
  }
});
</script>

<style lang="scss" scoped>
.categories-megamenu-wrapper {
  position: static;
  display: inline-flex;
  align-items: stretch;

  @media (max-width: 768px) {
    display: contents;
  }
}

/* Apple Style Trigger Button */
.categories-trigger-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.42rem 0.95rem;
  border-radius: 9999px;
  background: #f8fafc;
  color: #1e293b;
  font-weight: 600;
  font-size: 0.85rem;
  flex-grow: 1;
  height: auto;
  border: 1px solid #cbd5e1;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  user-select: none;

  @media (max-width: 768px) {
    display: none !important;
  }

  .categories-trigger-label {
    color: inherit;
    display: inline-block;
  }

  .categories-trigger-chevron {
    font-size: 0.68rem;
    transition: transform 0.25s ease;
    color: #64748b;
  }

  &:hover {
    background: #eef6fa;
    color: var(--primary-color, #386f99);
    border-color: var(--primary-color, #386f99);
    box-shadow: 0 4px 12px rgba(56, 111, 153, 0.12);

    .categories-trigger-label,
    .categories-trigger-chevron {
      color: var(--primary-color, #386f99);
    }
  }

  &--open {
    background: #eef6fa;
    color: var(--primary-color, #386f99);
    border-color: var(--primary-color, #386f99);
    box-shadow: 0 0 0 3px rgba(56, 111, 153, 0.18);

    .categories-trigger-label,
    .categories-trigger-chevron {
      color: var(--primary-color, #386f99);
    }
  }

  &--active {
    border-color: var(--primary-color, #386f99);
    color: var(--primary-color, #386f99);
  }
}

/* Backdrop Overlay (Frosted Apple Glass) */
.megamenu-backdrop {
  position: fixed;
  top: 80px;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 99998;
  transition: left 0.25s cubic-bezier(0.16, 1, 0.3, 1),
    width 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &.megamenu-sidebar-open {
    left: 250px;
    width: calc(100% - 250px);
    @media (max-width: 768px) {
      left: 0;
      width: 100%;
    }
  }

  &.megamenu-sidebar-closed {
    left: 64px;
    width: calc(100% - 64px);
    @media (max-width: 768px) {
      left: 0;
      width: 100%;
    }
  }
}

/* Mega-Menu Panel (Apple Style with Distinct Colored Top Border) */
.megamenu-panel {
  position: fixed;
  top: 80px;
  right: 0;
  background: #ffffff;
  // border-top: 3px solid var(--primary-color, #386f99);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding: 1.15rem 0 1.5rem 0;
  z-index: 99999;
  box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.12),
    0 4px 16px rgba(15, 23, 42, 0.04);
  max-height: calc(100dvh - 86px);
  overflow-y: auto;
  transition: left 0.25s cubic-bezier(0.16, 1, 0.3, 1),
    width 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f8fafc;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    &:hover {
      background: #94a3b8;
    }
  }

  &.megamenu-sidebar-open {
    left: 250px;
    width: calc(100% - 250px);
    @media (max-width: 768px) {
      left: 0;
      width: 100%;
    }
  }

  &.megamenu-sidebar-closed {
    left: 64px;
    width: calc(100% - 64px);
    @media (max-width: 768px) {
      left: 0;
      width: 100%;
    }
  }
}

.megamenu-container {
  max-width: 1800px;
  margin: 0 auto;
  padding: 0 1.75rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
}

/* Topbar */
.megamenu-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 0.85rem;
  margin-bottom: 1.1rem;
  border-bottom: 1px solid #f1f5f9;
  flex-wrap: wrap;

  .topbar-icon-box {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #f5f5f7;
    border: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color, #386f99);
    font-size: 0.85rem;
  }

  .topbar-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #1d1d1f;
    letter-spacing: -0.015em;
  }

  .topbar-count-badge {
    font-size: 0.7rem;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 9999px;
    background: #f5f5f7;
    color: #6e6e73;
    border: 1px solid rgba(0, 0, 0, 0.04);
  }
}

/* Apple Segmented Filter Tabs */
.category-tabs {
  .tab-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.3rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #6e6e73;
    background: #f5f5f7;
    border: 1px solid transparent;
    cursor: pointer;
    transition: all 0.18s ease;

    &:hover {
      background: #e8e8ed;
      color: #1d1d1f;
    }

    &--active {
      background: #1d1d1f;
      color: #ffffff;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);

      &:hover {
        background: #2d2d30;
        color: #ffffff;
      }
    }
  }
}

/* Apple Spotlight Style Search Box */
.search-box-wrapper {
  .search-input {
    width: 100%;
    padding: 0.42rem 2rem 0.42rem 2.2rem;
    background: #f5f5f7;
    border: 1px solid transparent;
    border-radius: 9999px;
    font-size: 0.8rem;
    font-weight: 500;
    color: #1d1d1f;
    outline: none;
    transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

    &::placeholder {
      color: #86868b;
    }

    &:focus {
      background: #ffffff;
      border-color: var(--primary-color, #386f99);
      box-shadow: 0 0 0 3px rgba(56, 111, 153, 0.14);
    }
  }

  .search-clear-btn {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #86868b;
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #1d1d1f;
    }
  }
}

/* Content Area */
.megamenu-content-wrapper {
  min-height: 140px;
}

/* Apple Main Grid */
.megamenu-main-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 320px;
    align-items: start;
  }
}

/* Categories Columns Grid */
.categories-columns-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;

  &.cols-1 {
    grid-template-columns: 1fr;
    max-width: 480px;
  }

  &.cols-2 {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr !important;
    gap: 0.75rem;
  }
}

.category-column {
  display: flex;
  flex-direction: column;
}

/* Category Column Header (Apple Style) */
.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.6rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid #f1f5f9;

  .category-header-icon {
    display: none;
  }

  .category-title {
    font-size: 0.85rem;
    font-weight: 700;
    color: #1d1d1f;
    letter-spacing: -0.01em;
  }

  .category-subtitle {
    font-size: 0.7rem;
    color: #86868b;
    margin-top: 1px;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 1024px) {
    cursor: pointer;
    user-select: none;
    padding: 0.65rem 0.85rem;
    border-radius: 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    margin-bottom: 0.4rem;
    transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

    &:hover {
      background: #f1f5f9;
      border-color: #cbd5e1;
    }

    &--open {
      background: transparent;
      border-radius: 0;
      border: none;
      border-top: 2px solid var(--primary-color, #386f99);
      padding: 0.65rem 0.35rem 0.35rem 0.35rem;
      margin-top: 0.4rem;
      margin-bottom: 0.35rem;

      &:hover {
        background: transparent;
      }

      .category-title {
        color: var(--primary-color, #386f99);
      }

      .category-collapse-chevron {
        color: var(--primary-color, #386f99);
      }
    }
  }
}

.category-collapse-chevron {
  font-size: 0.75rem;
  color: #64748b;
  transition: transform 0.25s ease;
}

/* Items List (Untitled UI / Apple Minimal List) */
.category-items-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.category-item-link {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.55rem 0.65rem;
  border-radius: 10px;
  background: transparent;
  border: 1px solid transparent;
  color: #1d1d1f;
  text-decoration: none;
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;

  .item-icon-box {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: #f5f5f7;
    border: 1px solid rgba(0, 0, 0, 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    color: #48484a;
    flex-shrink: 0;
    transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .item-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    line-height: 1.25;
  }

  .item-title-row {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .item-name {
    font-size: 0.85rem;
    font-weight: 600;
    color: #1d1d1f;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 0.15s ease;
  }

  .item-description {
    font-size: 0.725rem;
    color: #86868b;
    line-height: 1.25;
    margin-top: 1px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .item-arrow-icon {
    font-size: 0.65rem;
    color: #86868b;
    opacity: 0;
    transform: translateX(-4px);
    transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    align-self: center;
  }

  &:hover {
    background: #f5f5f7;

    .item-icon-box {
      background: var(--primary-color, #386f99);
      color: #ffffff;
      border-color: var(--primary-color, #386f99);
      box-shadow: 0 4px 10px rgba(56, 111, 153, 0.2);
    }

    .item-name {
      color: var(--primary-color, #386f99);
    }

    .item-arrow-icon {
      opacity: 1;
      transform: translateX(0);
      color: var(--primary-color, #386f99);
    }
  }

  &--active {
    background: #eef6fa;
    border-color: #d1e3ef;

    .item-icon-box {
      background: var(--primary-color, #386f99);
      color: #ffffff;
      border-color: var(--primary-color, #386f99);
    }

    .item-name {
      color: var(--primary-color, #386f99);
      font-weight: 700;
    }
  }
}

/* Featured Column & Promo Card (Untitled UI / Apple Style) */
.featured-column {
  display: flex;
  flex-direction: column;
}

.featured-card {
  background: #f5f5f7;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  padding: 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 8px 24px -6px rgba(0, 0, 0, 0.06);
  }

  .featured-header {
    .featured-title {
      font-size: 0.92rem;
      font-weight: 700;
      color: #1d1d1f;
      letter-spacing: -0.01em;
      line-height: 1.25;
    }

    .featured-desc {
      font-size: 0.75rem;
      color: #86868b;
      margin-top: 3px;
      line-height: 1.35;
    }
  }

  .featured-media-box {
    position: relative;
    width: 100%;
    height: 140px;
    border-radius: 12px;
    overflow: hidden;
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #334155 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
    transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);

    &:hover {
      transform: translateY(-2px);

      .media-play-btn {
        transform: scale(1.1);
        background: rgba(255, 255, 255, 0.35);
      }
    }

    .featured-media-bg {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      .media-glow {
        position: absolute;
        width: 160px;
        height: 160px;
        border-radius: 50%;
        background: radial-gradient(
          circle,
          rgba(56, 111, 153, 0.45) 0%,
          transparent 70%
        );
      }

      .media-illustration {
        color: rgba(255, 255, 255, 0.08);
        font-size: 4.5rem;
        transform: rotate(-10deg);
      }
    }

    .media-play-btn {
      position: relative;
      z-index: 2;
      width: 38px;
      height: 38px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.22);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
      transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .media-mockup-toolbar {
      position: absolute;
      bottom: 8px;
      left: 8px;
      right: 8px;
      padding: 0.35rem 0.65rem;
      border-radius: 8px;
      background: rgba(15, 23, 42, 0.65);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.12);
      display: flex;
      align-items: center;
      justify-content: space-between;
      z-index: 2;
    }
  }

  .featured-footer {
    display: flex;
    align-items: center;

    .featured-footer-link {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.8rem;
      font-weight: 700;
      color: #1d1d1f;
      text-decoration: none;
      transition: color 0.15s ease;

      &:hover {
        color: var(--primary-color, #386f99);
      }
    }
  }
}

/* Badges */
.category-badge,
.item-badge {
  font-size: 0.62rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 9999px;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  line-height: 1.2;

  &--accent,
  &--agentic {
    background: #ede9fe;
    color: #6d28d9;
    border: 1px solid #ddd6fe;
  }

  &--info {
    background: #e0f2fe;
    color: #0369a1;
    border: 1px solid #bae6fd;
  }

  &--warning {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
  }

  &--danger {
    background: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fecaca;
  }

  &--primary,
  &--success,
  &--new {
    background: #e0f0fa;
    color: #20557c;
    border: 1px solid #bce0f5;
  }
}

/* No Results State */
.no-results-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  text-align: center;

  .no-results-icon {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #f5f5f7;
    color: #86868b;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }

  .no-results-title {
    font-size: 0.85rem;
    color: #64748b;
    margin-bottom: 0.75rem;
    font-weight: 600;
  }

  .reset-search-btn {
    padding: 0.35rem 0.85rem;
    border-radius: 6px;
    background: #eef6fa;
    color: var(--primary-color, #386f99);
    border: 1px solid #d1e3ef;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--primary-color, #386f99);
      color: #ffffff;
    }
  }
}

:deep(.search-highlight) {
  background: #fef08a;
  color: #854d0e;
  font-weight: 800;
  border-radius: 2px;
  padding: 0 2px;
}

/* Transitions */
.megamenu-slide-enter-active {
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

.megamenu-slide-leave-active {
  transition: all 0.16s cubic-bezier(0.4, 0, 1, 1);
}

.megamenu-slide-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.megamenu-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
